<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-shtax-400 hover:text-shtax-600" :href="route('price_lists')">Cenniki</inertia-link>
      <span class="text-shtax-400 font-medium">/</span>
      {{ form.name }}
    </h1>

    <trashed-message v-if="price_list.deleted_at" class="mb-6" @restore="restore">
      Cennik jest oznaczony jako usunięty.
    </trashed-message>

    <div class="bg-white rounded-md shadow overflow-hidden max-w-3xl">
      <form @submit.prevent="update">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          
          <text-input v-model="form.name" :error="form.errors.name" class="pr-6 pb-8 w-full" label="Nazwa" />

          <multiselect-input v-model="form.type" :error="form.errors.type" class="pr-6 pb-8 w-full lg:w-full" label="Rodzaj" :options="types" track_by="id" opt_label="name" :multiple="false" :close_on_select="true" @input="onChange" />

          <text-input v-model="form.active_from" :error="form.errors.active_from" class="pr-6 pb-8 w-full lg:w-1/2" label="Od dnia" type="date" />
          <text-input v-model="form.active_to" :error="form.errors.active_to" class="pr-6 pb-8 w-full lg:w-1/2" label="Do dnia" type="date" />

          <shtax-checkbox class="pr-6 pb-8 w-1/2 lg:w-1/3" v-model:checked="form.is_hidden_on_lists" label="Ukryty na listach wyboru"/>

          <div class="pb-8 pr-6 w-full">
            <label class="form-label">Szczegóły:</label>
            <vue-editor v-model="form.details" />

            <div class="w-1/2 form-error">
              <span v-if="form.errors.details">{{ form.errors.details }}</span>
            </div>
          </div>

        </div>
        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center">
          <button v-if="!price_list.deleted_at && $page.props.can['delete admin module position']" class="text-red-600 hover:underline mr-5" tabindex="-1" type="button" @click="destroy">Usuń cennik</button>
          <loading-button :loading="form.processing" class="btn-shtax ml-auto" type="submit">Zaktualizuj dane</loading-button>
        </div>
      </form>
    </div>

    <h2 class="mt-12 font-bold text-xl md:text-2xl relative" v-if="form.type.id < 4">
        Pozycje cennika
        <div class="inline">
            <inertia-link class="btn-shtax ml-auto absolute right-0 top-0 disabled:opacity-50" :href="route('price_lists.items.create', {'price_list': price_list.id})" v-if="!price_list.deleted_at">
              <span>Nowa</span>
              <span class="hidden md:inline">pozycja</span>
            </inertia-link>
        </div>
    </h2>

    <div class="mt-6 bg-white rounded shadow overflow-x-auto" v-if="form.type.id == 3">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Opis</th>
          <th class="px-6 pt-6 pb-4">Wartość</th>
          <th class="px-6 pt-6 pb-4" colspan="2">Kwota minimum</th>
        </tr>
        <tr v-for="position in price_list.items" :key="position.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span>
                {{ position.description }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span>
                {{ position.fv_pos_value.replace('.', ',') }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span>
                {{ position.is_min_price_value ? 'Tak' : 'Nie' }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t w-px">
            <inertia-link class="px-4 flex items-center" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </inertia-link>
          </td>
        </tr>
        <tr v-if="price_list.items.length === 0">
          <td class="border-t px-6 py-4" colspan="3">Brak pozycji na cenniku.</td>
        </tr>
      </table>
    </div>

    <div class="mt-6 bg-white rounded shadow overflow-x-auto" v-if="form.type.id < 3">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Opis</th>
          <th class="px-6 pt-6 pb-4">Min liczba dokumentów</th>
          <th class="px-6 pt-6 pb-4">Max liczba dokumentów</th>
          <th class="px-6 pt-6 pb-4">Wartość</th>
          <th class="px-6 pt-6 pb-4">Dodatkowa opłata</th>
          <th class="px-6 pt-6 pb-4">Kwota dodatkowej opłaty</th>
          <th class="px-6 pt-6 pb-4" colspan="2">Kwota minimum</th>
        </tr>
        <tr v-for="position in price_list.items" :key="position.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t" colspan="3" v-if="position.description != null" >
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span>
                {{ position.description }}
              </span>
            </inertia-link>
          </td>
          <td v-else class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span>
                {{ position.description }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t" v-if="position.description == null">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span v-if="position.min_num_of_docs >= 0">
                {{ position.min_num_of_docs }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t" v-if="position.description == null">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span v-if="position.max_num_of_docs >= 0">
                {{ position.max_num_of_docs == 0 ? '-' : position.max_num_of_docs }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span>
                {{ position.fv_pos_value.replace('.', ',') }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span v-if="position.min_num_of_docs >= 0">
                {{ position.is_per_docs ? 'Tak' : 'Nie' }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span v-if="position.min_num_of_docs >= 0">
                {{ position.per_doc_value.replace('.', ',') }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})">
              <span>
                {{ position.is_min_price_value ? 'Tak' : 'Nie' }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t w-px">
            <inertia-link class="px-4 flex items-center" :href="route('price_lists.items.edit', {'price_list':price_list.id, 'item':position.id})" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </inertia-link>
          </td>
        </tr>
        <tr v-if="price_list.items.length === 0">
          <td class="border-t px-6 py-4" colspan="7">Brak pozycji na cenniku.</td>
        </tr>
      </table>
    </div>

  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import Icon from '@/Shared/Icon'
import MultiselectInput from '@/ShtaxComponents/MultiselectInput'
import ShtaxCheckbox from '@/ShtaxComponents/Checkbox'
import { VueEditor } from "vue2-editor";

export default {
  metaInfo() {
    return {
      title: `${this.price_list.name}`,
    }
  },
  components: {
    LoadingButton,
    TextInput,
    TrashedMessage,
    Icon,
    VueEditor,
    ShtaxCheckbox,
    MultiselectInput
  },
  layout: Layout,
  props: {
    types: Array,
    price_list: Object
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        name: this.price_list.name,
        type: this.price_list.type,
        active_from: this.price_list.active_from,
        active_to: this.price_list.active_to,
        details: this.price_list.details,
        is_hidden_on_lists: this.price_list.is_hidden_on_lists
      })
    }
  },
  methods: {
    update() {
      this.form.put(this.route('price_lists.update', {'price_list':this.price_list.id}))
    },
    destroy() {
      if (confirm('Jesteś pewien że chcesz usunąć wybrany cennik?')) {
        // this.$inertia.delete(this.route('price_lists.destroy', {'price_list':this.price_list.id}))
      }
    },
    restore() {
      if (confirm('Jesteś pewien że chcesz przywrócić wybrany cennik?')) {
        // this.$inertia.put(this.route('price_lists.restore', {'price_list':this.price_list.id}))
      }
    },
    onChange (value) {
      this.form.details = value.details
    },
  },

  computed: {
  }
}
</script>
